import { safeGetJSON, safeSetJSON } from "../safeStorage";

// Free plays, then an email. The one rule for who plays the arcade without
// limits lives in resolveArcadeAccess; decideStart turns it into what the
// Start button does. Posting to a leaderboard needs more than playing does:
// a member sign in or a confirmed email (see canPostScores).

export const FREE_RUNS = 3;
export const FREE_RUNS_KEY = "truevined-arcade:free-runs";

export type ArcadeUnlockReason = "member" | "admin" | "email" | "bot" | "ad" | "offline";

export interface ArcadeViewer {
  isAuthenticated: boolean;
  isAdmin: boolean;
  emailVerified: boolean;
}

export type UnlockCheckResult = { unlocked: boolean; status?: "pending" | "confirmed" | "unsubscribed" };

export interface ArcadeAccessInput {
  // Convex is configured for this build. Without it nothing is gated.
  backendReady: boolean;
  isBot: boolean;
  // A paid visit (?ad or ?adMode anywhere in this session).
  adSession: boolean;
  // viewer.me, undefined while it loads or when it failed.
  viewer: ArcadeViewer | undefined;
  storedEmail: string | null;
  hasUnlockToken: boolean;
  // checkUnlock for the stored email and token, undefined while it loads.
  unlockCheck: UnlockCheckResult | undefined;
}

export interface ArcadeAccess {
  unlocked: boolean;
  reason: ArcadeUnlockReason | null;
  // Still finding out; the Start button lets people play meanwhile.
  resolving: boolean;
  canPostScores: boolean;
  // The stored email is waiting for its confirm click.
  awaitingConfirmation: boolean;
}

// Games ask for less than downloads and artist pages do: an email entered on
// this device unlocks play straight away, confirmed or still pending, the
// same way the artist gate never shows its modal to a pending visitor. Only
// the server saying that address unsubscribed takes it back. Scores are
// different: they post only for a signed in member or a confirmed email
// whose unlock token is on this device, because a bare address proves nothing.
export function resolveArcadeAccess(input: ArcadeAccessInput): ArcadeAccess {
  const locked = { unlocked: false, reason: null, resolving: false, canPostScores: false, awaitingConfirmation: false } satisfies ArcadeAccess;
  if (!input.backendReady) return { ...locked, unlocked: true, reason: "offline" };
  const confirmedEmail = Boolean(input.storedEmail && input.hasUnlockToken && input.unlockCheck?.unlocked && input.unlockCheck.status === "confirmed");
  const memberCanPost = Boolean(input.viewer?.isAuthenticated && input.viewer.emailVerified);
  const canPostScores = memberCanPost || confirmedEmail;
  if (input.viewer?.isAdmin) return { ...locked, unlocked: true, reason: "admin", canPostScores };
  if (input.viewer?.isAuthenticated) return { ...locked, unlocked: true, reason: "member", canPostScores };
  if (input.storedEmail && input.unlockCheck?.status !== "unsubscribed") {
    const awaitingConfirmation = !confirmedEmail;
    return { ...locked, unlocked: true, reason: "email", canPostScores, awaitingConfirmation };
  }
  if (input.isBot) return { ...locked, unlocked: true, reason: "bot" };
  if (input.adSession) return { ...locked, unlocked: true, reason: "ad" };
  return { ...locked, resolving: input.viewer === undefined };
}

export type StartDecision =
  | { kind: "play"; counted: false }
  | { kind: "play"; counted: true; left: number }
  | { kind: "gate"; why: "free-runs-used" | "members-only" };

// What pressing Start (or Play again) does. Resume after a pause never comes
// through here, and neither do the demo, attract mode or thumbnails.
export function decideStart(input: { access: ArcadeAccess; used: number; membersOnly?: boolean }): StartDecision {
  if (input.access.unlocked || input.access.resolving) return { kind: "play", counted: false };
  if (input.membersOnly) return { kind: "gate", why: "members-only" };
  const used = Math.max(0, Math.floor(input.used));
  if (used < FREE_RUNS) return { kind: "play", counted: true, left: FREE_RUNS - used - 1 };
  return { kind: "gate", why: "free-runs-used" };
}

export const freeRunsLeft = (used: number) => Math.max(0, FREE_RUNS - Math.max(0, Math.floor(used)));

export function freeRunsLabel(left: number): string {
  if (left <= 0) return "No free plays left";
  return `${left} free ${left === 1 ? "play" : "plays"} left`;
}

interface FreeRunsState {
  used: number;
  updatedAt: number;
}

export function readFreeRunsUsed(): number {
  const stored = safeGetJSON<Partial<FreeRunsState>>(FREE_RUNS_KEY);
  const used = Number(stored?.used);
  return Number.isFinite(used) && used > 0 ? Math.floor(used) : 0;
}

export function recordFreeRun(now = Date.now()): number {
  const used = readFreeRunsUsed() + 1;
  safeSetJSON(FREE_RUNS_KEY, { used, updatedAt: now } satisfies FreeRunsState);
  return used;
}
